/**
 * Task 日志查看 Modal + 页面级接线 hook。
 *
 * 原先内联在 JobTaskList 里（4 个 useState + 一个 handler + 一大段 Modal JSX），
 * 感知报表页的 case 明细也要「看日志」，故按 ``useVersionTrace`` 的既有范式提到共用
 * 组件：状态、拉取、渲染收敛成一处，调用方只拿 ``openTaskLog`` 和 ``modal``。
 *
 * 用法::
 *
 *   const { openTaskLog, modal } = useTaskLogModal();
 *   // 表格里：<a onClick={() => openTaskLog(record.taskId)}>Task日志</a>
 *   // 页面尾：{modal}
 *
 * Coredump 堆栈折叠面板:
 *   默认收起,点击展开时才调后端 ``/task/logs/coredump-tail`` 抽栈(BOS 桶未开
 *   CORS,前端不能直连预签名 URL)。与 kit ``task logs --coredump`` 同款正则,
 *   在 backend ``task_service.query_task_coredump_tail`` 里 grep。
 */
import { type ReactNode, useCallback, useRef, useState } from 'react';
import { Alert, Button, Collapse, Modal, Spin, Tooltip, Typography } from 'antd';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';

import {
  getEvaluationFileUrls,
  getTaskCoredumpTail,
  getTaskLogs,
  type CoredumpTailResponse,
  type TaskLogResponse,
} from '@/apis/simulation';
import {
  buildRuntimeLogPrefix,
  RECORD_TIME_LOG_FILENAME,
  runtimeLogHint,
  runtimeLogLabel,
  selectRuntimeLogFiles,
  type RuntimeLogFile,
} from '@/utils/runtimeLogFiles';

/** 弹窗顶部的「标题: 值」信息行；``hint`` 非空时标题带悬停说明。 */
function InfoRow({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  const title = <strong>{label}:</strong>;
  return (
    <div style={{ marginBottom: 8 }}>
      {hint ? <Tooltip title={hint}>{title}</Tooltip> : title}
      {children}
    </div>
  );
}

/** 链接只给「下载 / 复制地址」两个动作，不铺开整串预签名 URL。 */
function LinkActions({ url }: { url: string }) {
  return (
    <>
      <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#5e6ad2', marginLeft: 8 }}>
        <DownloadOutlined style={{ marginRight: 4 }} />
        下载
      </a>
      <Typography.Text
        style={{ marginLeft: 16 }}
        copyable={{
          text: url,
          icon: [
            <span key="copy" style={{ color: '#5e6ad2' }}><CopyOutlined style={{ marginRight: 4 }} />复制地址</span>,
            <span key="done">已复制</span>,
          ],
          tooltips: false,
        }}
      />
    </>
  );
}

export function useTaskLogModal() {
  const [open, setOpen] = useState(false);
  const [taskId, setTaskId] = useState<number | null>(null);
  const [data, setData] = useState<TaskLogResponse | null>(null);
  const [loading, setLoading] = useState(false);

  // 产物里的 runtime_main 日志（如场景时间日志）：undefined=加载中，null=加载失败。
  // 与调度日志并行拉；每次打开/关闭递增请求序号，丢弃过期请求的迟到结果
  // （按 taskId 判断不够：同一 task 关闭再打开时旧请求仍会命中）。
  const [runtimeLogs, setRuntimeLogs] = useState<RuntimeLogFile[] | null | undefined>(undefined);
  const runtimeLogsSeqRef = useRef(0);

  // Coredump 折叠面板:展开态、抽栈结果、加载态、错误。
  // 每次开一个新 task 都清干净,避免看到上一个 task 的堆栈。
  const [coredumpKeys, setCoredumpKeys] = useState<string[]>([]);
  const [coredump, setCoredump] = useState<CoredumpTailResponse | null>(null);
  const [coredumpLoading, setCoredumpLoading] = useState(false);
  const [coredumpError, setCoredumpError] = useState<string | null>(null);

  const openTaskLog = useCallback(async (id: number) => {
    setTaskId(id);
    const seq = ++runtimeLogsSeqRef.current;
    setOpen(true);
    setData(null);
    setLoading(true);
    setRuntimeLogs(undefined);
    setCoredumpKeys([]);
    setCoredump(null);
    setCoredumpError(null);
    const runtimeLogsPromise = (async () => {
      try {
        const files = await getEvaluationFileUrls(
          [buildRuntimeLogPrefix(id)], 'output', { skipErrorHandler: true, download: true },
        );
        if (runtimeLogsSeqRef.current === seq) setRuntimeLogs(selectRuntimeLogFiles(id, files));
      } catch (err) {
        console.error('获取 runtime_main 日志下载链接失败:', err);
        if (runtimeLogsSeqRef.current === seq) setRuntimeLogs(null);
      }
    })();
    try {
      setData(await getTaskLogs(id));
    } catch (err) {
      console.error('加载Task日志失败:', err);
    } finally {
      setLoading(false);
    }
    await runtimeLogsPromise;
  }, []);

  const onCancel = useCallback(() => {
    setOpen(false);
    setData(null);
    setTaskId(null);
    runtimeLogsSeqRef.current += 1;
    setRuntimeLogs(undefined);
    setCoredumpKeys([]);
    setCoredump(null);
    setCoredumpError(null);
  }, []);

  const onCoredumpToggle = useCallback(
    async (keys: string | string[]) => {
      const arr = Array.isArray(keys) ? keys : [keys];
      setCoredumpKeys(arr);
      if (!arr.includes('coredump')) return;
      if (taskId == null) return;
      if (coredump || coredumpLoading) return;  // 已加载过 / 加载中,不重复拉
      setCoredumpLoading(true);
      setCoredumpError(null);
      try {
        setCoredump(await getTaskCoredumpTail(taskId));
      } catch (err) {
        console.error('Coredump 抽栈失败:', err);
        // axios 错误里带后端 detail;没有就退回 err.message
        const detail =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message
          || (err instanceof Error ? err.message : String(err));
        setCoredumpError(detail);
      } finally {
        setCoredumpLoading(false);
      }
    },
    [taskId, coredump, coredumpLoading],
  );

  /** 每份日志一行，版式同「日志下载链接」；加载中 / 失败 / 无时按场景时间日志占一行。 */
  const renderRuntimeLogRows = () => {
    const placeholder = (text: string, color: string) => (
      <InfoRow label={runtimeLogLabel(RECORD_TIME_LOG_FILENAME)}>
        <span style={{ marginLeft: 8, color }}>{text}</span>
      </InfoRow>
    );
    if (runtimeLogs === undefined) return placeholder('加载中…', '#9b9da3');
    if (runtimeLogs === null) return placeholder('获取下载链接失败', '#cf1322');
    if (runtimeLogs.length === 0) return placeholder('无', '#9b9da3');
    return runtimeLogs.map((f) => (
      <InfoRow key={f.path} label={runtimeLogLabel(f.filename)} hint={runtimeLogHint(f.filename)}>
        <LinkActions url={f.url} />
      </InfoRow>
    ));
  };

  const renderCoredumpBody = () => {
    if (coredumpLoading) {
      return (
        <div style={{ padding: 12 }}>
          <Spin /> <span style={{ marginLeft: 8 }}>后端下载并扫描日志尾部…</span>
        </div>
      );
    }
    if (coredumpError) {
      return <Alert type="error" showIcon message={`抽栈失败:${coredumpError}`} />;
    }
    if (!coredump) {
      return (
        <div style={{ color: '#9b9da3', padding: 12 }}>
          点击标题展开,开始扫描日志尾部找堆栈行。
        </div>
      );
    }
    const kbLabel = `${(coredump.scannedBytes / 1024).toFixed(0)} KB`;
    const scopeLabel = coredump.fromTail
      ? `已扫描日志尾部 ${kbLabel} / 约 ${coredump.scannedLines} 行`
      : `Range 不支持,已全量扫描 ${kbLabel} / ${coredump.scannedLines} 行`;
    return (
      <div>
        <div style={{ marginBottom: 8, color: '#9b9da3', fontSize: 12 }}>
          {scopeLabel},匹配 <strong style={{ color: '#e4e4e7' }}>{coredump.hits.length}</strong> 行
        </div>
        {coredump.hits.length === 0 ? (
          <Alert
            type="info"
            showIcon
            message="未匹配到堆栈 / fatal / 终止信号行"
            description="可能任务不是 coredump,或日志格式变了。可点上方「日志下载链接」自行查看。"
          />
        ) : (
          <div
            style={{
              padding: 12,
              backgroundColor: '#18181b',
              color: '#a1a1aa',
              borderRadius: 6,
              border: '1px solid #27272a',
              maxHeight: 320,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: "'JetBrains Mono', Consolas, monospace",
              fontSize: 12,
            }}
          >
            {coredump.hits.map(({ lineNo, line }) => (
              <div key={lineNo}>
                <span style={{ color: '#6b7280', marginRight: 8 }}>
                  {String(lineNo).padStart(5, ' ')}:
                </span>
                {line}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const modal = (
    <Modal
      title={`Task 日志 - Task ID: ${taskId}`}
      open={open}
      onCancel={onCancel}
      footer={[
        <Button key="close" onClick={onCancel}>
          关闭
        </Button>,
      ]}
      width={1000}
      style={{ top: 20 }}
    >
      <Spin spinning={loading}>
        {data && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <InfoRow label="日志下载链接">
                {data.logDownloadUrl ? (
                  <LinkActions url={data.logDownloadUrl} />
                ) : (
                  <span style={{ marginLeft: 8, color: '#9b9da3' }}>无</span>
                )}
              </InfoRow>
              {renderRuntimeLogRows()}
              <div style={{ marginBottom: 8 }}>
                <strong>S3路径:</strong>
                <span style={{ marginLeft: 8 }}>{data.logS3Path || '-'}</span>
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong>总行数:</strong>
                <span style={{ marginLeft: 8 }}>{data.totalLines || 0}</span>
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong>显示行数:</strong>
                <span style={{ marginLeft: 8 }}>
                  {data.tailLines || 0}(最后 {data.tailLines || 0} 行)
                </span>
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <strong>日志内容:</strong>
              <div
                style={{
                  marginTop: 8,
                  padding: 12,
                  backgroundColor: '#18181b',
                  color: '#a1a1aa',
                  borderRadius: 6,
                  border: '1px solid #27272a',
                  maxHeight: 500,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: "'JetBrains Mono', Consolas, monospace",
                  fontSize: 12,
                }}
              >
                {data.logContent || '无日志内容'}
              </div>
            </div>

            {/* Coredump 堆栈折叠面板:放到日志内容下方,默认收起,展开触发拉取。 */}
            <Collapse
              activeKey={coredumpKeys}
              onChange={onCoredumpToggle}
              style={{ marginTop: 16 }}
              items={[
                {
                  key: 'coredump',
                  label: (
                    <span>
                      <strong>堆栈(coredump 排查)</strong>
                      <span style={{ marginLeft: 8, color: '#9b9da3', fontSize: 12 }}>
                        展开时后端扫描日志尾部 2MB / 10000 行
                      </span>
                    </span>
                  ),
                  children: renderCoredumpBody(),
                },
              ]}
            />
          </div>
        )}
      </Spin>
    </Modal>
  );

  return { openTaskLog, modal };
}
